import { useState } from 'react';
import { Tooltip, Typography } from '@mui/material';

/**
 * TruncatedText component
 *
 * Shows text on a single line. If it is wider than the available space it is cut off
 * with an ellipsis ("...") and the full text is shown in a tooltip on hover.
 * Use it for long names / emails in tables so they can't stretch the table width.
 *
 * @param {string} text - The text to display (and show in the tooltip).
 * @param {number|string} maxWidth - Max width before truncating. Omit it when the parent (e.g. a flex item with minWidth: 0) already limits the width.
 * @param {string} placement - Tooltip position. Defaults to "top".
 * @param {object} sx - Extra styles for the text, e.g. { fontWeight: "bold" }.
 * @param {node} children - Optional styled content to show instead of `text` (the tooltip still shows `text`).
 * @param {boolean} onlyWhenTruncated - Show the tooltip only when the text is actually cut off. Defaults to false.
 * @param {object} tooltipSx - Optional styles for the tooltip bubble (e.g. to match a page's design).
 *
 * @returns {JSX.Element} The TruncatedText component.
 */
export const TruncatedText = ({ text, maxWidth, placement = "top", sx = {}, children, onlyWhenTruncated = false, tooltipSx, ...props }) => {
    const [open, setOpen] = useState(false);
    const handleOpen = (event) => {
        const el = event?.currentTarget;
        if (!onlyWhenTruncated || (el && el.scrollWidth > el.clientWidth)) setOpen(true);
    };

    return (
        <Tooltip
            title={text || ""}
            placement={placement}
            open={open}
            onOpen={handleOpen}
            onClose={() => setOpen(false)}
            slotProps={{
                popper: {
                    sx: { "&[data-popper-reference-hidden]": { visibility: "hidden", pointerEvents: "none" } },
                },
                ...(tooltipSx ? { tooltip: { sx: tooltipSx } } : {}),
            }}
        >
            <Typography
                noWrap
                sx={{ fontSize: "inherit", maxWidth, ...sx }}
                {...props}
            >
                {children ?? text}
            </Typography>
        </Tooltip>
    );
};
